import React from 'react';

const ICONS = { flight:'fa-plane', hotel:'fa-hotel', carservice:'fa-car-side', car:'fa-car', ground:'fa-van-shuttle', train:'fa-train' };
const COLORS = { flight:'#1E40AF', hotel:'#06B6D4', carservice:'#06B6D4', car:'#fbbf24', ground:'#f472b6', train:'#a78bfa' };
const TYPE_LABELS = { flight:'Flight', hotel:'Hotel', carservice:'Car Service', car:'Car Rental', ground:'Ground', train:'Train' };

function fmt12(t) {
  if (!t) return '';
  if (/[APap][Mm]/.test(t)) return t;
  const [h, m] = t.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return t;
  return `${h % 12 || 12}:${String(m).padStart(2,'0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

function getTime(r) {
  if (r.type === 'flight') return r.flightDepartureTime;
  if (r.type === 'carservice') return r.carServiceTime;
  if (r.type === 'train') return r.departureTime;
  return null;
}

function getTitle(r) {
  if (r.type === 'flight') {
    if (r.flightType === 'private') return r.operator || r.airline || (r.tailNumber ? `Private ${r.tailNumber}` : 'Private Flight');
    return `${r.airline || ''} ${r.flightNumber || ''}`.trim() || 'Flight';
  }
  if (r.type === 'hotel') return r.hotelName || 'Hotel';
  if (r.type === 'carservice') return r.carServiceCompany || 'Car Service';
  if (r.type === 'car') return r.carCompany || 'Car Rental';
  if (r.type === 'ground') return r.driverName || 'Ground Transport';
  if (r.type === 'train') return r.trainCompany || 'Train';
  return TYPE_LABELS[r.type] || r.type;
}

function getRoute(r) {
  if (r.type === 'flight') return [r.departureAirport, r.arrivalAirport].filter(Boolean).join(' → ');
  if (r.type === 'carservice') return [r.carServicePickup, r.carServiceDropoff].filter(Boolean).map(s => s.split(',')[0]).join(' → ');
  if (r.type === 'ground') return [r.pickupLocation, r.dropoffLocation].filter(Boolean).map(s => s.split(',')[0]).join(' → ');
  if (r.type === 'car') return [r.carPickupLocation, r.carDropoffLocation].filter(Boolean).map(s => s.split(',')[0]).join(' → ');
  if (r.type === 'train') return [r.trainFrom, r.trainTo].filter(Boolean).join(' → ');
  if (r.type === 'hotel') return r.hotelAddress ? r.hotelAddress.split(',').slice(0,2).join(',') : '';
  return '';
}

function getDetail(r) {
  const parts = [];
  if (r.type === 'flight') {
    if (r.flightType === 'private' && r.tailNumber) parts.push(r.tailNumber);
    if (r.flightType === 'private' && r.aircraftType) parts.push(r.aircraftType);
    if (r.flightType !== 'private' && r.seat) parts.push('Seat ' + r.seat);
    if (r.flightConfirmation) parts.push('Conf: ' + r.flightConfirmation);
  } else if (r.type === 'hotel') {
    if (r.roomType) parts.push(r.roomType);
    if (r.hotelConfirmation) parts.push('Conf: ' + r.hotelConfirmation);
  } else if (r.type === 'carservice') {
    if (r.carServiceDriver) parts.push('Driver: ' + r.carServiceDriver);
    if (r.carServiceConfirmation) parts.push('Conf: ' + r.carServiceConfirmation);
  } else if (r.type === 'train') {
    if (r.trainConfirmation) parts.push('Conf: ' + r.trainConfirmation);
  } else if (r.type === 'car') {
    if (r.carConfirmation) parts.push('Conf: ' + r.carConfirmation);
  } else if (r.type === 'ground') {
    if (r.driverPhone) parts.push(r.driverPhone);
  }
  return parts.join(' · ');
}

function TimelineNode({ record: r, isLast, onClick, onEdit, onDelete, colorMap }) {
  const typeColor = COLORS[r.type] || '#1E40AF';
  const icon = ICONS[r.type] || 'fa-circle';
  const time = getTime(r);
  const title = getTitle(r);
  const route = getRoute(r);
  const detail = getDetail(r);

  // Parse comma-separated passengers and resolve colors
  const passengers = (r.passenger || '').split(',').map(s => s.trim()).filter(Boolean);
  const passengerColors = passengers.map(name => {
    if (!colorMap) return null;
    const key = Object.keys(colorMap).find(k => k.toLowerCase() === name.toLowerCase());
    return key ? colorMap[key] : null;
  });
  const primaryColor = passengerColors.find(c => c) || null;
  const color = primaryColor || typeColor;

  return (
    <div style={{ display:'flex', gap:0, cursor:'pointer', position:'relative' }} onClick={() => onClick(r)}>
      {/* Time column */}
      <div style={{ width:62, flexShrink:0, textAlign:'right', paddingRight:16, paddingTop:2 }}>
        {time && (
          <p style={{ fontSize:12, fontWeight:700, color:'var(--text2)', margin:0, lineHeight:1.2 }}>
            {fmt12(time)}
          </p>
        )}
      </div>

      {/* Timeline rail */}
      <div style={{ width:32, flexShrink:0, display:'flex', flexDirection:'column', alignItems:'center', position:'relative' }}>
        {/* Icon dot */}
        <div style={{
          width:32, height:32, borderRadius:10, background:`${color}18`,
          border:`2px solid ${color}`,
          display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, zIndex:1
        }}>
          <i className={`fa-solid ${icon}`} style={{ fontSize:13, color }}></i>
        </div>
        {/* Connector line */}
        {!isLast && (
          <div style={{
            width:2, flex:1, minHeight:16,
            background:`linear-gradient(${color}66, ${COLORS[r.type]}22)`,
          }} />
        )}
      </div>

      {/* Content */}
      <div style={{
        flex:1, marginLeft:12, paddingBottom: isLast ? 0 : 20,
        minWidth:0,
      }}>
        <div style={{
          background:'var(--bg)', borderRadius:12, border:'1px solid var(--border)',
          padding:'12px 16px',
          transition:'border-color 0.15s, background 0.15s',
        }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = color; e.currentTarget.style.background = `${color}08`; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--bg)'; }}
        >
          {/* Title row */}
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom: (route || detail || passengers.length) ? 4 : 0 }}>
            <p style={{ fontSize:15, fontWeight:800, color:'var(--text)', margin:0, flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
              {title}
            </p>
            {r.type === 'flight' && r.departureAirport && r.arrivalAirport && (
              <span style={{ fontSize:13, fontWeight:700, color, letterSpacing:0.5, flexShrink:0 }}>
                {r.departureAirport} → {r.arrivalAirport}
              </span>
            )}
          </div>

          {/* Passenger badges */}
          {passengers.length > 0 && (
            <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:4 }}>
              {passengers.map((name, idx) => {
                const pc = passengerColors[idx] || 'var(--text3)';
                const initials = name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0,2);
                return (
                  <div key={name} style={{ display:'inline-flex', alignItems:'center', gap:5,
                    padding:'2px 8px 2px 3px', borderRadius:20, background:`${typeof pc === 'string' && pc.startsWith('#') ? pc : ''}10`,
                    border:`1px solid ${typeof pc === 'string' && pc.startsWith('#') ? pc+'33' : 'var(--border)'}` }}>
                    <div style={{
                      width:18, height:18, borderRadius:9, background: pc,
                      display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0,
                    }}>
                      <span style={{ fontSize:8, fontWeight:800, color:'#fff', lineHeight:1 }}>{initials}</span>
                    </div>
                    <span style={{ fontSize:11, fontWeight:600, color: typeof pc === 'string' && pc.startsWith('#') ? pc : 'var(--text2)' }}>
                      {name.split(' ')[0]}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Route / address (non-flight) */}
          {route && r.type !== 'flight' && (
            <p style={{ fontSize:13, color:'var(--text2)', margin:'2px 0 0', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
              <i className="fa-solid fa-route" style={{ fontSize:10, marginRight:6, color:'var(--text3)' }}></i>
              {route}
            </p>
          )}

          {/* Flight times row */}
          {r.type === 'flight' && (r.flightDepartureTime || r.flightArrivalTime) && (
            <div style={{ display:'flex', alignItems:'center', gap:8, margin:'4px 0 0' }}>
              {r.flightDepartureTime && (
                <span style={{ fontSize:12, color:'var(--text2)' }}>
                  <i className="fa-solid fa-plane-departure" style={{ fontSize:9, marginRight:4, color:'var(--text3)' }}></i>
                  {fmt12(r.flightDepartureTime)}
                </span>
              )}
              {r.flightDepartureTime && r.flightArrivalTime && (
                <span style={{ fontSize:10, color:'var(--text3)' }}>—</span>
              )}
              {r.flightArrivalTime && (
                <span style={{ fontSize:12, color:'var(--text2)' }}>
                  <i className="fa-solid fa-plane-arrival" style={{ fontSize:9, marginRight:4, color:'var(--text3)' }}></i>
                  {fmt12(r.flightArrivalTime)}
                </span>
              )}
            </div>
          )}

          {/* Detail line */}
          {detail && (
            <p style={{ fontSize:12, color:'var(--text3)', margin:'4px 0 0', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
              {detail}
            </p>
          )}

          {/* Greeter badge for flights */}
          {r.type === 'flight' && r.greeterName && (
            <div style={{ display:'inline-flex', alignItems:'center', gap:5, marginTop:6, padding:'3px 10px', borderRadius:20, background:'rgba(251,191,36,0.1)', border:'1px solid rgba(251,191,36,0.2)' }}>
              <i className="fa-solid fa-hand-wave" style={{ fontSize:9, color:'#fbbf24' }}></i>
              <span style={{ fontSize:11, fontWeight:600, color:'#fbbf24' }}>
                {r.greeterName}{r.greeterPhone ? ' · ' + r.greeterPhone : ''}
              </span>
            </div>
          )}

          {/* Notes */}
          {r.notes && (
            <p style={{ fontSize:11, color:'var(--text3)', margin:'6px 0 0', fontStyle:'italic' }}>{r.notes}</p>
          )}

          {/* Edit / Delete */}
          {(onEdit || onDelete) && (
            <div style={{ display:'flex', gap:12, marginTop:8, paddingTop:8, borderTop:'1px solid var(--border)' }}>
              {onEdit && (
                <button onClick={e => { e.stopPropagation(); onEdit(r); }}
                  style={{ background:'none', border:'none', cursor:'pointer', fontSize:12, color:'var(--blue)', fontFamily:'inherit', fontWeight:600, padding:0, display:'flex', alignItems:'center', gap:4 }}>
                  <i className="fa-solid fa-pen" style={{ fontSize:10 }}></i> Edit
                </button>
              )}
              {onDelete && (
                <button onClick={e => { e.stopPropagation(); onDelete(r.id); }}
                  style={{ background:'none', border:'none', cursor:'pointer', fontSize:12, color:'var(--red)', fontFamily:'inherit', fontWeight:600, padding:0, display:'flex', alignItems:'center', gap:4 }}>
                  <i className="fa-solid fa-trash" style={{ fontSize:10 }}></i> Delete
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function TravelTimeline({ records, onSelect, onEdit, onDelete, colorMap }) {
  if (!records || records.length === 0) return null;

  return (
    <div style={{ padding:'4px 0' }}>
      {records.map((r, i) => (
        <TimelineNode
          key={r.id}
          record={r}
          isLast={i === records.length - 1}
          onClick={onSelect}
          onEdit={onEdit}
          onDelete={onDelete}
          colorMap={colorMap}
        />
      ))}
    </div>
  );
}
